import React, { useState, useEffect } from 'react';
import './FrequencyModal.css';

function FrequencyModal({ frequency, onSave, onClose }) {
  const [formData, setFormData] = useState({
    name: '',
    color_hex: '#c2a45a',
    description: '',
    icon: '✨',
  });
  const [audioFiles, setAudioFiles] = useState([]);
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const [audioPremium, setAudioPremium] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (frequency) {
      setFormData({
        name: frequency.name,
        color_hex: frequency.color_hex,
        description: frequency.description,
        icon: frequency.icon,
      });
      setAudioFiles(frequency.audio_files || []);
    }
  }, [frequency]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.name.trim()) {
      setError('Bitte geben Sie einen Namen ein');
      return;
    }

    if (!/^#[0-9A-Fa-f]{6}$/.test(formData.color_hex)) {
      setError('Bitte geben Sie eine gültige Hex-Farbe ein (z.B. #c2a45a)');
      return;
    }

    if (!formData.description.trim()) {
      setError('Bitte geben Sie eine Beschreibung ein');
      return;
    }

    if (!formData.icon.trim()) {
      setError('Bitte geben Sie ein Icon ein');
      return;
    }

    setLoading(true);

    try {
      await onSave(formData);
    } catch (err) {
      setError(err.message || 'Fehler beim Speichern');
      setLoading(false);
    }
  };

  const handleAudioChange = async (e) => {
    const file = e.target.files[0];
    if (!file || !frequency) return;

    // Validate file type
    const validTypes = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/ogg', 'audio/webm'];
    if (!validTypes.includes(file.type) && !file.name.match(/\.(mp3|wav|ogg|webm)$/i)) {
      setError('Bitte wählen Sie eine gültige Audio-Datei (MP3, WAV, OGG, WebM)');
      return;
    }

    setUploadingAudio(true);
    setError('');

    try {
      const apiClient = (await import('../../utils/api')).default;
      const result = await apiClient.uploadAudio(frequency.id, file, audioPremium);
      // Add full audio object to list
      const audioEntry = {
        filename: result.filename,
        is_premium: result.is_premium
      };
      setAudioFiles([...audioFiles, audioEntry]);
      e.target.value = ''; // Reset file input
      setAudioPremium(false); // Reset checkbox
    } catch (err) {
      setError(err.message || 'Fehler beim Hochladen der Audio-Datei');
    } finally {
      setUploadingAudio(false);
    }
  };

  const handleDeleteAudio = async (filename) => {
    if (!window.confirm('Möchten Sie diese Audio-Datei wirklich löschen?')) {
      return;
    }

    try {
      const apiClient = (await import('../../utils/api')).default;
      await apiClient.deleteAudio(frequency.id, filename);
      // Handle both string and object format
      setAudioFiles(audioFiles.filter(f => {
        const fname = typeof f === 'string' ? f : f.filename;
        return fname !== filename;
      }));
    } catch (err) {
      setError(err.message || 'Fehler beim Löschen der Audio-Datei');
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Common emoji icons for quick selection
  const commonIcons = ['✨', '⭐', '🌙', '☀️', '⚡', '🔥', '💧', '🌿', '💚', '❤️', '🧡', '💙', '💜', '💛', '🎯', '⚖️', '🧘', '🎵'];

  return (
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div className="modal-container" data-testid="frequency-modal">
        <div className="modal-header">
          <h2 className="modal-title">
            {frequency ? 'Frequenz bearbeiten' : 'Neue Frequenz erstellen'}
          </h2>
          <button
            onClick={onClose}
            className="modal-close"
            data-testid="modal-close"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          {error && (
            <div className="error-message" data-testid="modal-error">
              {error}
            </div>
          )}

          {/* Name */}
          <div className="form-group">
            <label htmlFor="name">Name *</label>
            <input
              id="name"
              name="name"
              type="text"
              value={formData.name}
              onChange={handleChange}
              placeholder="z.B. Balance"
              maxLength={100}
              required
              data-testid="name-input"
            />
          </div>

          {/* Color */}
          <div className="form-group">
            <label htmlFor="color_hex">Farbe (Hex) *</label>
            <div className="color-input-group">
              <input
                id="color_hex"
                name="color_hex"
                type="text"
                value={formData.color_hex}
                onChange={handleChange}
                placeholder="#c2a45a"
                pattern="^#[0-9A-Fa-f]{6}$"
                required
                data-testid="color-input"
              />
              <input
                type="color"
                value={formData.color_hex}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, color_hex: e.target.value }))
                }
                className="color-picker"
                data-testid="color-picker"
              />
              <div
                className="color-preview-large"
                style={{ backgroundColor: formData.color_hex }}
              ></div>
            </div>
            <span className="form-hint">
              Hex-Format: #RRGGBB (z.B. #c2a45a für Gold)
            </span>
          </div>

          {/* Icon */}
          <div className="form-group">
            <label htmlFor="icon">Icon / Emoji *</label>
            <input
              id="icon"
              name="icon"
              type="text"
              value={formData.icon}
              onChange={handleChange}
              placeholder="✨"
              maxLength={10}
              required
              data-testid="icon-input"
            />
            <div className="icon-preview">
              <span className="preview-label">Vorschau:</span>
              <span className="preview-icon">{formData.icon || '✨'}</span>
            </div>
            <div className="icon-suggestions">
              <span className="suggestions-label">Vorschläge:</span>
              <div className="suggestions-list">
                {commonIcons.map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    className="icon-suggestion"
                    onClick={() => setFormData((prev) => ({ ...prev, icon: emoji }))}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="form-group">
            <label htmlFor="description">Beschreibung *</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Beschreiben Sie die Wirkung dieser Frequenz..."
              maxLength={500}
              rows={4}
              required
              data-testid="description-input"
            />
            <span className="form-hint">
              {formData.description.length} / 500 Zeichen
            </span>
          </div>

          {/* Audio Upload Section - Only for editing existing frequency */}
          {frequency && (
            <div className="form-group audio-section">
              <label>🔊 Audio-Bibliothek (Optional)</label>
              
              <div className="audio-upload-area">
                <div className="upload-controls">
                  <label className="premium-toggle">
                    <input
                      type="checkbox"
                      checked={audioPremium}
                      onChange={(e) => setAudioPremium(e.target.checked)}
                    />
                    <span>💎 Nur für Premium-User</span>
                  </label>
                </div>
                
                <input
                  type="file"
                  accept="audio/mp3,audio/mpeg,audio/wav,audio/ogg,audio/webm"
                  onChange={handleAudioChange}
                  id="audio-file"
                  disabled={uploadingAudio}
                  data-testid="audio-file-input"
                />
                {uploadingAudio && <span className="uploading-text">⏳ Hochladen...</span>}
                <span className="form-hint">
                  Unterstützte Formate: MP3, WAV, OGG, WebM - Mehrere Dateien möglich
                  {audioPremium && ' (💎 Premium-Only)'}
                </span>
              </div>

              {audioFiles.length > 0 && (
                <div className="audio-list">
                  <div className="audio-list-header">
                    📚 {audioFiles.length} {audioFiles.length === 1 ? 'Datei' : 'Dateien'} hochgeladen
                  </div>
                  {audioFiles.map((audioItem, index) => {
                    const filename = typeof audioItem === 'string' ? audioItem : audioItem.filename;
                    const isPremium = typeof audioItem === 'object' ? audioItem.is_premium : false;
                    return (
                      <div key={filename} className="audio-item">
                        <span className="audio-item-number">{index + 1}.</span>
                        <span className="audio-item-icon">🎵</span>
                        <span className="audio-item-name">{filename}</span>
                        {isPremium && (
                          <span className="premium-badge-small">💎</span>
                        )}
                        <button
                          type="button"
                          onClick={() => handleDeleteAudio(filename)}
                          className="btn-delete-audio-small"
                          data-testid={`delete-audio-${index}`}
                        >
                          🗑️
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="modal-actions">
            <button
              type="button"
              onClick={onClose}
              className="btn-modal-cancel"
              data-testid="cancel-button"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              className="btn-modal-save"
              disabled={loading}
              data-testid="save-button"
            >
              {loading ? 'Speichern...' : 'Speichern'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default FrequencyModal;
